import type { ComponentPropsWithRef, ReactElement } from 'react';
import { cn } from '../util/classes.ts';

/**
 * EmptyState — the shared "nothing here" primitive, composed from slots.
 *
 * @example
 * <EmptyState>
 *     <EmptyStateIcon><SearchIcon /></EmptyStateIcon>
 *     <EmptyStateTitle>Nichts gefunden</EmptyStateTitle>
 *     <EmptyStateDescription>Prüfe die Schreibweise.</EmptyStateDescription>
 * </EmptyState>
 */
export type EmptyStateProps = ComponentPropsWithRef<'div'>;

export function EmptyState({ className, ...props }: EmptyStateProps): ReactElement {
    return (
        <div
            data-slot="empty-state"
            className={cn(
                'flex flex-col items-center gap-cozy px-relaxed py-12 text-center text-content-weaker',
                className,
            )}
            {...props}
        />
    );
}

export type EmptyStateIconProps = ComponentPropsWithRef<'span'>;

export function EmptyStateIcon({ className, ...props }: EmptyStateIconProps): ReactElement {
    return (
        <span
            data-slot="empty-state-icon"
            aria-hidden
            className={cn("*:[svg:not([class*='size-'])]:size-6", className)}
            {...props}
        />
    );
}

export type EmptyStateTitleProps = ComponentPropsWithRef<'p'>;

export function EmptyStateTitle({ className, ...props }: EmptyStateTitleProps): ReactElement {
    return (
        <p
            data-slot="empty-state-title"
            className={cn('text-body text-content-weaker', className)}
            {...props}
        />
    );
}

export type EmptyStateDescriptionProps = ComponentPropsWithRef<'p'>;

export function EmptyStateDescription({ className, ...props }: EmptyStateDescriptionProps): ReactElement {
    return <p data-slot="empty-state-description" className={cn('text-caption', className)} {...props} />;
}

export type EmptyStateActionsProps = ComponentPropsWithRef<'div'>;

export function EmptyStateActions({ className, ...props }: EmptyStateActionsProps): ReactElement {
    return (
        <div
            data-slot="empty-state-actions"
            className={cn('mt-cozy flex flex-wrap justify-center gap-cozy', className)}
            {...props}
        />
    );
}
